import { Component, Input, OnInit } from '@angular/core';
import { Adjustment } from 'src/app/model/project/adjustment';
import { Repo } from 'src/app/model/project/repo';
import { SoldRented } from 'src/app/model/project/sold-rented';
import { Location } from '@angular/common';
import { SnackService } from 'src/app/services/snack.service';
import { TabsService } from 'src/app/services/tabs.service';
import { SubProjectControllerRequestService } from 'src/app/services/api/sub-project-controller-request.service';
import ProjectHelper from 'src/app/helpers/project-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import Product from 'src/app/model/product/product';
import { StringHelper } from 'src/app/helpers/string-helper';
import { IPBoardHelper } from 'src/app/helpers/ip-board-helper';
import AssetHelper from '../../../../../helpers/asset-helper';

@Component({
  selector: 'client-adjustment-actions',
  templateUrl: './client-adjustment-actions.component.html',
  styleUrls: ['./client-adjustment-actions.component.scss'],
})
export class ClientAdjustmentActionsComponent implements OnInit {
  @Input()
  type: string;

  @Input()
  adjustment: Adjustment | Repo | SoldRented;

  @Input()
  projectId: number;

  @Input()
  minDate: Date;

  @Input()
  products: Product[] = [];

  submitting: boolean = false;

  constructor(
    private subProjectService: SubProjectControllerRequestService,
    private _location: Location,
    private snackService: SnackService,
    private tabsService: TabsService
  ) {}

  ngOnInit(): void {}

  goBack() {
    this._location.back();
  }

  private calculateProjectHasIPBoards(): boolean {
    return (
      CollectionHelper.isPopulated(this.products) &&
      this.products.some((p) => ProductHelper.isIPBoard(p))
    );
  }

  async submit() {
    this.submitting = true;
    const projectHasIPBoards = this.calculateProjectHasIPBoards();
    if (
      this.adjustment.projectPlacement.placeASAP != true &&
      this.adjustment.projectPlacement.placementEarliestDate == null
    ) {
      this.submitting = false;
      this.snackService.showError(
        'Gelieve een datum te kiezen of ASAP aan te duiden'
      );
      return;
    }
    if (
      this.adjustment.projectPlacement.placeASAP != true &&
      this.adjustment.projectPlacement.placementEarliestDate != null &&
      this.adjustment.projectPlacement.placementEarliestDate < this.minDate
    ) {
      this.submitting = false;
      this.snackService.showError(
        'Datum mag niet sneller dan 3 werkdagen zijn!'
      );
      return;
    }
    if (
      ProjectHelper.mustHaveAppointmentForPlacementFloor(
        this.adjustment.projectPlacement.placementFloor
      ) &&
      !(
        this.adjustment.projectPlacement.appointment ||
        this.adjustment.projectPlacement.collectKey
      ) &&
      (this.type === 'adjustment' ||
        (this.adjustment as Repo).repoByInpress == true ||
        (this.adjustment as SoldRented).installByInpress == true)
    ) {
      this.submitting = false;
      this.snackService.showError(
        'Gelieve ofwel een afspraak aan te vragen of aan te geven dat er een sleutel moet worden afgehaald'
      );
      return;
    }
    if (!this.adjustment.projectPlacement.appointment) {
      this.resetAppointmentFields();
    }
    if (!this.adjustment.projectPlacement.collectKey) {
      this.resetCollectKeyFields();
    }
    if (this.type === 'repo') {
      if ((this.adjustment as Repo).repoByInpress === false) {
        this.adjustment.projectPlacement.placeASAP = null;
        delete this.adjustment.projectPlacement.placementEarliestDate;

        this.adjustment.projectPlacement.appointment = null;
        this.resetAppointmentFields();
      }
      if (
        StringHelper.isEmpty(this.adjustment.extraInfoAdjustment) &&
        projectHasIPBoards &&
        !this.products.some((p) => IPBoardHelper.representsDivergentBoard(p))
      ) {
        this.adjustment.approved = true;
      }
      this.subProjectService
        .postClientCompletedRepo(this.projectId, this.adjustment)
        .then((res) => {
          this.showSuccessAndRedirect('Weghalen', res);
        })
        .finally(() => (this.submitting = false));
    } else if (this.type === 'adjustment') {
      if (StringHelper.isEmpty(this.adjustment.extraInfoAdjustment)) {
        this.submitting = false;
        this.snackService.showError('Gelieve de aanpassing te beschrijven');
        return;
      }
      const adjustmentDto = { ...this.adjustment };
      if (adjustmentDto.projectPlacement.projectPlacementAssets != null) {
        const OfflineAssets = [];
        for (const a of adjustmentDto.projectPlacement.projectPlacementAssets) {
          const offlineAssetDto = { ...a.asset };
          offlineAssetDto['formSection'] = a.formSection;
          offlineAssetDto.base64 = await AssetHelper.extractBase64(
            offlineAssetDto['localFile']
          );
          OfflineAssets.push(offlineAssetDto);
        }
        adjustmentDto.projectPlacement.projectPlacementAssets = OfflineAssets;
      }

      this.subProjectService
        .postClientCompletedAdjsutment(this.projectId, adjustmentDto)
        .then((res) => {
          this.showSuccessAndRedirect('Aanpassing', res);
        })
        .finally(() => (this.submitting = false));
    } else {
      const soldRented = this.adjustment as SoldRented;
      if (
        soldRented.signSticker == null &&
        StringHelper.isEmpty(soldRented.otherSticker) &&
        soldRented.installByInpress
      ) {
        this.submitting = false;
        this.snackService.showError('Gelieve een sticker te kiezen');
        return;
      }

      if (soldRented.installByInpress === false) {
        soldRented.projectPlacement.placeASAP = null;
        delete soldRented.projectPlacement.placementEarliestDate;

        soldRented.projectPlacement.appointment = null;
        this.resetAppointmentFields();

        soldRented.otherSticker = null;
        soldRented.signSticker = null;
        soldRented.extraInfoAdjustment = null;
      }
      if (this.shouldAutoApproveSoldRented(soldRented, projectHasIPBoards)) {
        soldRented.approved = true;
      }
      this.subProjectService
        .postClientCompletedSoldRented(this.projectId, soldRented)
        .then((res) => {
          this.showSuccessAndRedirect('Verkocht/verhuurd', res);
        })
        .finally(() => (this.submitting = false));
    }
  }

  private shouldAutoApproveSoldRented(
    soldRented: SoldRented,
    projectHasIPBoards: boolean
  ) {
    if (!projectHasIPBoards) {
      return false;
    }
    if (
      soldRented.signSticker != null &&
      (!CollectionHelper.isPopulated(soldRented.signSticker.examples) ||
        !soldRented.signSticker.examples.some((e) => !e.archived))
    ) {
      return false;
    }
    return (
      soldRented.signSticker != null &&
      StringHelper.isEmpty(soldRented.extraInfoAdjustment)
    );
  }

  showSuccessAndRedirect(type: string, id: number) {
    this.snackService.showSuccess(type + ' succesvol aangevraagd');
    this.tabsService.closeAfterSubmit(
      this.tabsService.getActiveTab(),
      '/client-corner/lopende'
    );
  }

  resetAppointmentFields() {
    this.adjustment.projectPlacement.appointmentContact = null;
    this.adjustment.projectPlacement.needAppointmentInfo = null;
    this.adjustment.projectPlacement.appointmentName = null;
    this.adjustment.projectPlacement.appointmentPhoneNumber = null;
    this.adjustment.projectPlacement.appointmentEmail = null;
  }

  resetCollectKeyFields() {
    this.adjustment.projectPlacement.collectKeyFrom = null;
    this.adjustment.projectPlacement.collectKeyExtraInfo = null;
  }
}
